import { Context, createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useConfig } from '~/core/configuration/configContext';
import { QuerySetConfig } from '~/core/configuration/configuration-set.type';
import { Configuration } from '~/core/configuration/configuration.type';
import { useSERPState } from '~/core/state/SERPStateContext';
import { QuerySet_ControlsState } from '~/core/state/types/state-controls.type';
import { QuerySetResults_State, Results_State } from '~/core/state/types/state-results.schema';
import { QuerySet_State } from '~/core/state/types/state-sets.type';
import { findQuerySetData } from '~/core/state/utils/findQuerySetData';
import SRError from '~/utils/errors/srError';

interface IQuerysetContext<T extends QuerySetResults_State> {
    state: QuerySet_State;
    config: QuerySetConfig | undefined;
    result: T;
    controls: QuerySet_ControlsState;
}

const QuerySetContext = createContext<IQuerysetContext<QuerySetResults_State> | undefined>(undefined);

interface IQuerySetContextProvider {
    querySetId: string;
    state?: QuerySet_State;
    children: ReactNode;
}

export const QuerySetContextProvider = ({ children, state: propsState, querySetId }: IQuerySetContextProvider) => {
    const serpConfig = useConfig();
    const serpState = useSERPState();

    const value = useMemo(() => {
        const { querySetState, querySetControls, querySetConfig, querySetResults } = findQuerySetData<
            QuerySetResults_State,
            Configuration
        >()(propsState?.querySet.identifier ?? querySetId, {
            state: serpState,
            config: serpConfig,
            results: serpState.results.sets,
        });

        return {
            state: querySetState,
            controls: querySetControls,
            config: querySetConfig,
            result: querySetResults,
        };
    }, [propsState, querySetId, serpConfig, serpState]);

    return <QuerySetContext.Provider value={value}>{children}</QuerySetContext.Provider>;
};

export const useQuerySet = <T extends QuerySetResults_State>() => {
    const value = useContext(QuerySetContext as Context<IQuerysetContext<T> | undefined>);

    if (!value) {
        throw new Error('useQueryset must be used withing a QuerysetContextProvider');
    }

    return value;
};
